import React, { useCallback, useRef, useState } from 'react';

/**
 * Promise-based "Sold" prompt: Annual Premium + First Policy Draft Date.
 * The draft date anchors the 12-month persistency texts (Draft −48h, +14d, +90d, +5mo, +11mo).
 *
 *   const { askSale, saleModal } = useQuickSale();
 *   const sale = await askSale();   // { annualPremium, firstDraftDate: 'YYYY-MM-DD' } or null if cancelled
 *   ...
 *   return <>{...}{saleModal}</>;
 *
 * `askPremium()` is kept for older callers and resolves to just the premium number (or null).
 *
 * @returns {{
 *   askSale: () => Promise<{annualPremium: number, firstDraftDate: string}|null>,
 *   askPremium: () => Promise<number|null>,
 *   saleModal: JSX.Element|null,
 * }}
 */
export default function useQuickSale() {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [draftDate, setDraftDate] = useState('');
  const resolver = useRef(null);

  const askSale = useCallback(() => new Promise((resolve) => {
    resolver.current = resolve;
    setValue('');
    setDraftDate('');
    setOpen(true);
  }), []);

  const askPremium = useCallback(async () => (await askSale())?.annualPremium ?? null, [askSale]);

  function finish(result) {
    setOpen(false);
    resolver.current?.(result);
    resolver.current = null;
  }

  const amount = parseFloat(value);
  const valid = Number.isFinite(amount) && amount > 0 && /^\d{4}-\d{2}-\d{2}$/.test(draftDate);

  const saleModal = open ? (
    <div
      className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="quick-sale-modal"
      onMouseDown={(e) => { if (e.target === e.currentTarget) finish(null); }}
    >
      <form
        className="panel p-6 w-full max-w-sm space-y-4"
        onSubmit={(e) => { e.preventDefault(); if (valid) finish({ annualPremium: amount, firstDraftDate: draftDate }); }}
      >
        <h3 className="font-semibold text-lg">Record sale</h3>
        <div>
          <label className="label" htmlFor="quick-sale-premium">Policy Annual Premium ($)</label>
          <div className="relative mt-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50 text-sm">$</span>
            <input
              id="quick-sale-premium"
              className="input w-full pl-7"
              type="number"
              min="1"
              step="0.01"
              autoFocus
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="1200"
            />
          </div>
          <p className="text-xs text-white/50 mt-2">
            Automatically calculates your commission and updates vendor ROI.
          </p>
        </div>
        <div>
          <label className="label" htmlFor="quick-sale-draft-date">First Policy Draft Date</label>
          <input
            id="quick-sale-draft-date"
            className="input w-full mt-1"
            type="date"
            value={draftDate}
            onChange={(e) => setDraftDate(e.target.value)}
            data-testid="quick-sale-draft-date"
          />
          <p className="text-xs text-white/50 mt-2">
            Schedules the 12-month persistency texts: a reminder 48 hours before the draft, then check-ins at 14 days, 90 days, 5 months and 11 months.
          </p>
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={() => finish(null)}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={!valid}>Mark Sold</button>
        </div>
      </form>
    </div>
  ) : null;

  return { askSale, askPremium, saleModal };
}
